import { useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import {
  myLeaveBalances,
  myLeaves,
  applyLeave,
  cancelMyLeave,
} from "../../api/leaveService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { nowIst, formatDate } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

const STATUS_BADGE = {
  pending: "badge-warning",
  approved: "badge-success",
  rejected: "badge-danger",
  cancelled: "badge-neutral",
};

export default function MyLeavesPage() {
  const year = nowIst().year();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [applyOpen, setApplyOpen] = useState(false);

  const balances = useApi(() => myLeaveBalances(year));
  const leaves = useApi(() => myLeaves({ page, per_page: perPage }), [page, perPage]);

  const rows = paginatedRows(leaves.data);
  const meta = paginatedMeta(leaves.data);
  const balanceRows = balances.data?.data?.balances || [];

  const onCancel = async (l) => {
    if (!window.confirm("Cancel this leave application?")) return;
    try {
      await cancelMyLeave(l.id);
      showToast("success", "Application cancelled");
      leaves.reload();
      balances.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">My Leaves</h1>
          <div className="page-sub">Balances for {year} and your applications.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setApplyOpen(true)}>
            <Icon name="plus" size={14} /> Apply for leave
          </button>
        </div>
      </div>

      {/* Balance cards */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {balances.loading ? (
          <LoadingBlock rows={1} height={70} />
        ) : balanceRows.length === 0 ? (
          <div className="t-muted text-sm">No leave types configured yet — ask your admin.</div>
        ) : (
          balanceRows.map((b) => (
            <div key={b.leave_type_id} className="kpi">
              <div className="kpi-label">{b.leave_type}{b.is_paid ? "" : " (unpaid)"}</div>
              <div className="kpi-value">{b.remaining}</div>
              <div className="kpi-delta t-muted">of {b.allocated} · used {b.used}</div>
            </div>
          ))
        )}
      </div>

      {/* Applications */}
      <div className="card">
        <div className="card-body no-pad">
          {leaves.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="calendar" title="No applications yet" hint="Apply for leave and it will show up here." />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Dates</th>
                  <th className="t-num">Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Reviewed</th>
                  <th style={{ width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id}>
                    <td className="t-primary">{l.leave_type?.name}</td>
                    <td>
                      {formatDate(l.start_date)}
                      {l.start_date !== l.end_date ? ` → ${formatDate(l.end_date)}` : ""}
                      {l.is_half_day ? " (½ day)" : ""}
                    </td>
                    <td className="t-num">{Number(l.days)}</td>
                    <td className="t-muted" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.reason}>
                      {l.reason}
                    </td>
                    <td><span className={`badge ${STATUS_BADGE[l.status] || "badge-neutral"}`}>{l.status}</span></td>
                    <td className="t-muted text-xs">
                      {l.reviewer?.name ? `${l.reviewer.name}${l.admin_note ? ` · ${l.admin_note}` : ""}` : "—"}
                    </td>
                    <td>
                      {l.status === "pending" && (
                        <button className="icon-btn" title="Cancel" onClick={() => onCancel(l)} style={{ color: "var(--danger)" }}>
                          <Icon name="x" size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination meta={meta} page={page} onPageChange={setPage} perPage={perPage} onPerPageChange={setPerPage} disabled={leaves.loading} />
        </div>
      </div>

      <ApplyLeaveDrawer
        open={applyOpen}
        balances={balanceRows}
        onClose={() => setApplyOpen(false)}
        onSaved={() => {
          setApplyOpen(false);
          leaves.reload();
          balances.reload();
        }}
      />
    </>
  );
}

function ApplyLeaveDrawer({ open, balances, onClose, onSaved }) {
  const [typeId, setTypeId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const singleDay = start && start === end;
  const remaining = balances.find((b) => String(b.leave_type_id) === String(typeId))?.remaining;

  const submit = async () => {
    const next = {};
    if (!typeId) next.leave_type_id = "Pick a leave type.";
    if (!start) next.start_date = "Pick a start date.";
    if (!end) next.end_date = "Pick an end date.";
    if (!reason.trim()) next.reason = "A short reason is required.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await applyLeave({
        leave_type_id: Number(typeId),
        start_date: start,
        end_date: end,
        is_half_day: singleDay ? halfDay : false,
        reason: reason.trim(),
      });
      showToast("success", "Leave application submitted");
      setTypeId(""); setStart(""); setEnd(""); setHalfDay(false); setReason("");
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Apply for leave"
      subtitle="Weekly offs and holidays inside the range don't use up your balance."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Submitting…" : "Submit"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Leave type</label>
          <SearchableSelect
            value={typeId}
            onChange={(v) => setTypeId(v ?? "")}
            options={balances.map((b) => ({
              id: b.leave_type_id,
              label: b.leave_type,
              hint: `${b.remaining} left`,
            }))}
            placeholder="Select…"
            searchPlaceholder="Search leave types…"
            width="100%"
          />
          {errors.leave_type_id && <div className="field-error">{String(errors.leave_type_id)}</div>}
          {remaining != null && Number(remaining) <= 0 && (
            <div className="field-error">No balance left for this type.</div>
          )}
        </div>

        <div className="row gap-md">
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">From</label>
            <input type="date" className="input" value={start} onChange={(e) => { setStart(e.target.value); if (!end || e.target.value > end) setEnd(e.target.value); }} />
            {errors.start_date && <div className="field-error">{String(errors.start_date)}</div>}
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="field-label">To</label>
            <input type="date" className="input" min={start || undefined} value={end} onChange={(e) => setEnd(e.target.value)} />
            {errors.end_date && <div className="field-error">{String(errors.end_date)}</div>}
          </div>
        </div>

        {singleDay && (
          <div className="row gap-sm">
            <input id="half-day" type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} />
            <label htmlFor="half-day" style={{ cursor: "pointer", marginBottom: 0 }}>
              <div className="t-primary text-sm fw-500">Half day (0.5)</div>
            </label>
          </div>
        )}

        <div className="field">
          <label className="field-label">Reason</label>
          <textarea className="textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A short note for your admin" />
          {errors.reason && <div className="field-error">{String(errors.reason)}</div>}
        </div>
      </div>
    </Drawer>
  );
}
